import * as BABYLON from 'babylonjs';
import PanoDome from './PanoDome';
import PanoCamera from './PanoCamera';
import PanoEvents from '../Enumerations/ViewerEnums';
import PanoConstants from '../constants/ViewerConstants';
import Compass from '../components/Compass';
import DepthMap from './DepthMap';
import MeasurementManager from './MeasurementManager';
import BoxManager from './BoxManager';
declare const InstallTrigger: any; // for FireFox detection

interface PanoViewerOptions {
    measurementColor?: string;
    imageRotation?: number;
    initialDirection?: BABYLON.Vector3;
}

class PanoViewer {

    public static readonly MinZoomLevel = 0.2;
    public static readonly DefaultZoomLevel = 1.0;
    public static readonly MaxZoomLevel = 2.0;

    public events: EventTarget;

    private _scene: BABYLON.Scene;
    private _engine: BABYLON.Engine;
    private _canvas: HTMLCanvasElement;
    private _dome: PanoDome;

    private _divContainer: HTMLDivElement;

    private _compass: Compass;
    public _depthMap: DepthMap;

    private _measurementManager: MeasurementManager;
    private _boxManager: BoxManager;

    private _displayMode: DisplayMode;
    private _measurementType: MeasurementType;
    private _lastCameraDegrees: number;

    private _options: PanoViewerOptions;

    public get camera() { return <PanoCamera>this._scene?.cameras[0]; }
    public get scene() { return this._scene; }
    public get boxManager() { return this._boxManager; }

    public get cameraDirectionDegrees() { return this._lastCameraDegrees; }
    public get cameraFov() { return this.camera.fov }
    public get cameraAlpha() { return this.camera.alpha; }
    public get cameraBeta() { return this.camera.beta; }

    public get displayMode() { return this._displayMode; }
    public get measurementType() { return this._measurementType; }
    public get cameraDirection() {

        return this.camera.getWorldDirection();
    }
    constructor(div: HTMLDivElement, image: HTMLImageElement, imageDirection: IVector3D, overrideDirection: IVector3D, options?: PanoViewerOptions) {

        this.events = new EventTarget();

        this._divContainer = div;
        this._options = {
            measurementColor: options?.measurementColor ?? MeasurementManager.DefaultValidColor
        }

        var baseDirection = new BABYLON.Vector3(0, 0, 1);
        this._initializeBabylon(image, baseDirection, options?.imageRotation);

        // WHY IS Z AND Y SWAPPED HERE?
        const imageDirectionVector = options?.initialDirection ?? new BABYLON.Vector3(imageDirection.X, imageDirection.Z, imageDirection.Y);
        if (overrideDirection != null) {
            
            this.alignCamera(overrideDirection as any);
        }
        else {
            this.alignCamera(imageDirectionVector);
        }
        this._resizeCanvas();

        this._displayMode = DisplayMode.Navigation;

        this._compass = new Compass();
        this._compass.createCompass(this._divContainer);

        this._boxManager = new BoxManager({ kind: 'pano', viewer: this });

        this.camera.events.addEventListener(PanoEvents.Camera.CAMERA_CHANGED, this._handleCameraChange);
        this._handleCameraChange();

        this._measurementManager = new MeasurementManager(this, this._options.measurementColor);
    }

    public dispose() {
        this._removeEventListeners();

        this._measurementManager?.dispose();
        this._measurementManager = null;

        this._boxManager?.dispose();
        this._boxManager = null;
        
        this._scene.dispose();
        this._engine.dispose();

        this._scene = null;
        this._engine = null;
    }

    public alignCamera(vector: BABYLON.Vector3) {

        var cam = this._scene.activeCamera as any;

        if (cam._imageRotation != 0) {
            var m = new BABYLON.Matrix();
            var rotQuat = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), cam._imageRotation);
            rotQuat.toRotationMatrix(m);
            vector = BABYLON.Vector3.TransformCoordinates(vector, m);
        }

        var currentTarget = <BABYLON.Vector3>cam.target;
        var newPos = currentTarget.subtract(vector.scale(cam.radius));

        cam._currentTarget = (currentTarget);
        cam.setPosition(newPos);

        cam._computeViewMatrix();
        cam._getViewMatrix();
    }

    public changeMeasurementColor(newColor: string) {
        if (this._options.measurementColor != newColor) {
            this._options.measurementColor = newColor;
            this._measurementManager?.setMeasurementColor(newColor);
        }
    }

    public setMeasurements(measurements: any) {
        this._measurementManager?.setMeasurements(measurements);
    }

    public setDepthMap(depthMap: DepthMap) {
        this._depthMap = depthMap;
    }

    public toggleCompass(toggle: boolean) {
        this._compass.toggleCompass(toggle);
    }

    public async changeCameraDirection(imageDirection: BABYLON.Vector3, fov?: number, animate?: boolean) {
        // Calculate the new alpha (angle in the horizontal plane)
        let directionToTarget = imageDirection.normalizeToNew();
        directionToTarget = BABYLON.Vector3.TransformCoordinates(directionToTarget, this.camera.getImageRotationMatrix());
        let newAlpha = Math.atan2(directionToTarget.x, directionToTarget.z);
        newAlpha = -1 * Math.PI / 2 - newAlpha;

        // Calculate the new beta (angle in the vertical plane)
        const directionGroundProjection = Math.sqrt(directionToTarget.x * directionToTarget.x + directionToTarget.z * directionToTarget.z);
        let newBeta = Math.atan2(directionGroundProjection, directionToTarget.y);

        if (animate) {
            await this._animateCameraPropertiesChange(newAlpha, newBeta);
            await this._animateCameraFOVChange(fov);
            await new Promise(resolve => setTimeout(resolve, 200));
        }
        else {
            this.setCameraProperties(newAlpha, newBeta);
        }
    }

    public setCameraProperties(alpha: number, beta: number, fov?: number) {
        this.camera.alpha = alpha;
        this.camera.beta = beta;
        if (fov) {
            this.camera.fov = fov;
        }
    }

    public setViewerImage(image: HTMLImageElement) {
        this._measurementManager?.clearMeasurements();
        this._dome.changeTexture(image);
    }

    public setImageRotation(imageRotation: number = Math.PI / 2) {
        const cameraDirection = this.camera.getWorldDirection();
        this.camera.setImageRotation(imageRotation);
        this.alignCamera(cameraDirection);
    }

    public getScreenshot(width: number, height: number, callbackFn: (base64Image: string) => void) {
        const activeCamera = this._scene.activeCamera as BABYLON.Camera & {
            updateCache?: (force?: boolean) => void;
            _computeViewMatrix?: (force?: boolean) => BABYLON.Matrix;
            _getViewMatrix?: () => BABYLON.Matrix;
        };
        const renderCanvas = this._engine.getRenderingCanvas();
        const originalRenderWidth = this._engine.getRenderWidth();
        const originalRenderHeight = this._engine.getRenderHeight();
        const minimumCaptureWidth = 1280;
        const requestedAspectRatio = height > 0 ? width / height : 1;
        const captureWidth = Math.max(width, minimumCaptureWidth);
        const captureHeight = Math.max(
            height,
            Math.round(captureWidth / Math.max(requestedAspectRatio, 0.000001))
        );
        const originalCanvasStyleWidth = renderCanvas?.style.width;
        const originalCanvasStyleHeight = renderCanvas?.style.height;
        let restored = false;

        const refreshCameraMatrices = () => {
            activeCamera?.getProjectionMatrix?.(true);
            activeCamera?.updateCache?.(true);
            activeCamera?._computeViewMatrix?.(true);
            activeCamera?._getViewMatrix?.();
        };

        const restoreRenderState = () => {
            if (restored) {
                return;
            }

            restored = true;

            if (renderCanvas) {
                renderCanvas.style.width = originalCanvasStyleWidth;
                renderCanvas.style.height = originalCanvasStyleHeight;
            }

            this._engine.setSize(originalRenderWidth, originalRenderHeight);
            refreshCameraMatrices();
            this._scene.render();
        };

        if (renderCanvas) {
            renderCanvas.style.width = `${captureWidth}px`;
            renderCanvas.style.height = `${captureHeight}px`;
        }

        this._engine.setSize(captureWidth, captureHeight);
        refreshCameraMatrices();
        this._scene.render();

        try {
            BABYLON.Tools.CreateScreenshot(this._engine, this._scene.activeCamera, {
                width: captureWidth,
                height: captureHeight
            }, (base64Image) => {
                restoreRenderState();

                if (captureWidth === width && captureHeight === height) {
                    callbackFn(base64Image);
                    return;
                }

                const image = new Image();
                image.onload = () => {
                    const thumbnailCanvas = document.createElement('canvas');
                    thumbnailCanvas.width = width;
                    thumbnailCanvas.height = height;

                    const context = thumbnailCanvas.getContext('2d');
                    if (!context) {
                        callbackFn(base64Image);
                        return;
                    }

                    context.imageSmoothingEnabled = true;
                    context.imageSmoothingQuality = 'high';
                    context.drawImage(image, 0, 0, captureWidth, captureHeight, 0, 0, width, height);
                    callbackFn(thumbnailCanvas.toDataURL('image/png'));
                };
                image.onerror = () => callbackFn(base64Image);
                image.src = base64Image;
            });
        }
        catch (e) {
            restoreRenderState();
            throw e;
        }
    }

    public convertPositionToSphereCoordinates(position: BABYLON.Vector3) {
        return this._depthMap.convertPositionToSphereCoordinates(position);
    }
    public convertScreenToLocalCoordinate(pointerX: number, pointerY: number) {
        return this._depthMap.convertScreenToLocalCoordinate(pointerX, pointerY);
    }
    public convertLocalToScreenCoordinate(worldPosition: BABYLON.Vector3): { pointerX: number, pointerY: number } {
        return this._depthMap.convertLocalToScreenCoordinate(worldPosition);
    }
    public worldCoordinateInformation(worldCoordinate: BABYLON.Vector3) {
        const scanPosition = (this._depthMap as any)._imagePosition;
        const cameraRotMatrix = this.camera.getImageRotationMatrix();

        const dir = worldCoordinate.subtract(scanPosition).normalize();
        const rotatedDir = BABYLON.Vector3.TransformCoordinates(dir, cameraRotMatrix);
        const finalPos = rotatedDir.scale(PanoDome.DomeRadius);

        return { imageCoordinate: finalPos, distance: worldCoordinate.subtract(scanPosition).length() }
    }

    public setMeasurementType(mode: MeasurementType) {
        if (this._measurementType == mode)
            return;

        this._measurementType = mode;

        if (this._measurementManager != null)
            this._measurementManager.setMeasurementMode(this._measurementType);
    }
    public setDisplayMode(mode: DisplayMode) {
        if (this._displayMode == mode) {
            return;
        }

        /*
        if (mode == DisplayMode.Measurement) {
            this._measurementManager.setMeasurementMode(this._measurementType);
        }
        else {
            this._measurementManager.setMeasurementMode(null);
        }
        */

        if (mode == DisplayMode.TagVolumeMove) {
            this._boxManager.setPositioningMode("move");
        }
        else if (mode == DisplayMode.TagVolumeScale) {
            this._boxManager.setPositioningMode("scale");
        }
        else {
            this._boxManager.setPositioningMode("none");
        }

        if (mode == DisplayMode.CreateAnnotation || mode == DisplayMode.PositionAnnotation) {
            this._canvas.parentElement.style.cursor = 'crosshair';
        }
        else {
            this._canvas.parentElement.style.cursor = '';
         }

        this._displayMode = mode;
    }

    private async _animateCameraPropertiesChange(newAlpha: number, newBeta: number) {
        const camera = this.camera;

        // Normalize new alpha
        newAlpha = newAlpha % (2 * Math.PI);
        if (newAlpha < 0) newAlpha += 2 * Math.PI;

        // Normalize current alpha
        let currentAlpha = camera.alpha % (2 * Math.PI);
        if (currentAlpha < 0) currentAlpha += 2 * Math.PI;

        // Determine the shortest path
        const deltaAlpha = newAlpha - currentAlpha;
        if (deltaAlpha > Math.PI) {
            newAlpha -= 2 * Math.PI;
        } else if (deltaAlpha < -Math.PI) {
            newAlpha += 2 * Math.PI;
        }

        // Create the animation object
        const alphaAnimation = new BABYLON.Animation("cameraAlphaAnimation", "alpha", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
        alphaAnimation.setKeys([
            { frame: 0, value: currentAlpha },
            { frame: 60, value: newAlpha } // 1.0 seconds at 60 fps
        ]);

        // Create and set up the beta animation
        const betaAnimation = new BABYLON.Animation("cameraBetaAnimation", "beta", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
        betaAnimation.setKeys([
            { frame: 0, value: camera.beta },
            { frame: 60, value: newBeta }
        ]);

        // Attach the animations to the camera
        camera.animations = [alphaAnimation, betaAnimation];

        // Start the animation
        await this._scene.beginAnimation(camera, 0, 60, false).waitAsync();
    }

    private async _animateCameraFOVChange(newFOV: number) {
        const camera = this.camera;

        // Create the animation object
        const fovAnimation = new BABYLON.Animation("cameraFOVAnimation", "fov", 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);

        // Set the keyframes for the animation
        const keys = [];
        keys.push({ frame: 0, value: camera.fov });
        keys.push({ frame: 60, value: newFOV }); // 1.0 seconds at 60 fps

        // Assign the keys to the animation
        fovAnimation.setKeys(keys);

        // Attach the animation to the camera
        camera.animations = [];
        camera.animations.push(fovAnimation);

        // Start the animation
        await this._scene.beginAnimation(camera, 0, 60, false, 1).waitAsync();
    }

    private _addEventListeners() {
        window.addEventListener('resize', this._resizeCanvas);
    }

    private _removeEventListeners() {
        window.removeEventListener('resize', this._resizeCanvas);
    }

    private _updateCompass = (camDegrees: { rotatedDegrees: number, upDegrees: number, upAxis: string }) => {
        this._compass._updateCompass({
            rotatedDegrees: camDegrees.rotatedDegrees,
            upDegrees: camDegrees.upDegrees + "° " + camDegrees.upAxis
        });
    }

    private _handleCameraChange = () => {
        const camDegrees = this.camera.getCameraDegrees();
        this._updateCompass(camDegrees);

        this._lastCameraDegrees = camDegrees.rotatedDegrees;
        this.events?.dispatchEvent(new Event(PanoEvents.Viewer.VIEWER_DIRECTION_CHANGED));
    }

    private _resizeCanvas = () => {
        var width = this._canvas.parentElement.clientWidth;
        var height = this._canvas.parentElement.clientHeight;

        if ((this._canvas.width != width || this._canvas.height != height)) {

            this._canvas.style.width = width + 'px';
            this._canvas.style.height = height + 'px';

            //this is a hack to call the babylon engine to resize the 3d canvas.
            this._engine.setSize(width, height);

            this.events.dispatchEvent(new Event(PanoEvents.Viewer.VIEWER_RESIZED));
        }
    }

    private _initializeBabylon(image: HTMLImageElement, imageDirection: any, imageRotation: number = Math.PI / 2) {

        var canvas3dContainer = document.createElement('div');
        canvas3dContainer.className = 'vt-3d-container';
        canvas3dContainer.style.width = '100%';
        canvas3dContainer.style.height = '100%';
        canvas3dContainer.style.display = 'flex';
        canvas3dContainer.style.cursor = 'default';
        canvas3dContainer.style.justifyContent = 'center';

        // Set 3d canvas styles
        var canvas3d = this._canvas = document.createElement('canvas');
        canvas3d.style.width = '100%';
        canvas3d.style.height = '100%';
        canvas3d.style.padding = '0';
        canvas3d.style.margin = '0';
        canvas3d.style.backgroundColor = 'transparent';
        canvas3d.style.cursor = 'default';
        canvas3d.style.alignSelf = 'center';
        canvas3d.style.outline = '2px solid black';

        canvas3dContainer.appendChild(canvas3d);

        this._divContainer.appendChild(canvas3dContainer);

        this._addEventListeners();

        this._engine = new BABYLON.Engine(
            this._canvas,
            true
        );

        // There is a problem with drawing into the canvas (needed for scaling down large
        // image/texture sizes) in FireFox. The default canvas size for the scale-down operation
        // is 16384, which FireFox however cannot handle. Even 8192 causes occassional problems.
        // Using 6144 seems to be fine; if not, 4096 should definitely be safe.
        var isFirefox = typeof InstallTrigger !== 'undefined';
        if (isFirefox) {
            this._engine.getCaps().maxTextureSize = 6144;
        }
        
        this._canvas.addEventListener("contextmenu", function (e) {
            e.preventDefault();
        });

        this._scene = new BABYLON.Scene(this._engine);

        var camera = new PanoCamera("Camera", 0, 0, 1, new BABYLON.Vector3(0, 0, 1), this._scene, imageRotation);
        camera.position = new BABYLON.Vector3(0, 0, 0);
        camera.getProjectionMatrix(true)
        camera.updateCache(true);
        this.alignCamera(imageDirection);
        camera.attachControl(this._canvas, true);

        // Adjust the double click delay to 500ms (default is 300ms)
        BABYLON.Scene.DoubleClickDelay = 500;

        (camera as any).inputs.attached.pointers.buttons = [0, 1] as any;

        // Disable panning
        camera.panningSensibility = 0;

        // Detach default mousewheel handler, so that we can use our own mousewheel handling below
        camera.inputs.attached.mousewheel.detachControl();

        // Disable pinching
        (camera.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
        (camera.inputs.attached.pointers as any).multiTouchPanning = false;
        camera.pinchToPanMaxDistance = 1e10;

        // Create an ambient light instance and set its intensity
        const ambientLight = new BABYLON.HemisphericLight("ambientLight", new BABYLON.Vector3(0, 1, 0), this._scene);
        ambientLight.intensity = 0.7; // Adjust the intensity as needed

        // Keep credentialed image loading for viewer media paths that are not fetch-based.
        BABYLON.Tools.CorsBehavior = "use-credentials";

        const dome = new PanoDome(image, this._scene);
        dome.mesh.renderingGroupId = PanoConstants.RenderingGroups.DomeRenderingGroupId;
        dome.fovMultiplier = PanoViewer.DefaultZoomLevel;
        this._dome = dome;

        const events = this.events;
        this._scene.onPointerObservable.add(function (e) {
            if (dome === undefined) {
                return;
            }

            let zoomLevel = camera.fov + (e.event as any).wheelDelta * -0.00025;

            if (zoomLevel < PanoViewer.MinZoomLevel) {
                zoomLevel = PanoViewer.MinZoomLevel;
            }
            else if (zoomLevel > PanoViewer.MaxZoomLevel) {
                zoomLevel = PanoViewer.MaxZoomLevel;
            }

            if (camera.fov != zoomLevel) {
                camera.fov = zoomLevel;
                events?.dispatchEvent(new Event(PanoEvents.Viewer.VIEWER_FOV_CHANGED));
            }
        }, BABYLON.PointerEventTypes.POINTERWHEEL);

        this._engine.runRenderLoop(() => {
            this._scene?.render();
        });
    }
}

export default PanoViewer;
